import { motion } from "framer-motion";
import {
  PencilLine, Layers3, Shuffle, Scale, Boxes, Gem,
  Hammer, Camera, Sparkles, LibraryBig,
} from "lucide-react";

const FEATURES = [
  { icon: PencilLine, title: "Sketch to Jewellery Render", desc: "Drop a hand sketch — receive a photoreal render, finished and lit." },
  { icon: Layers3, title: "Multi-View Technical Sheets", desc: "Silhouette-locked front, 45°, 90° and 180° views — the CAD bench's dream reference." },
  { icon: Boxes, title: "Facetra 3D · CAD Mesh", desc: "Photogrammetric 3D mesh from your multiview — GLB, STL, OBJ, FBX. Standard or 8K PBR." },
  { icon: Camera, title: "PhotoRoom · Studio Touchup", desc: "Raw DSLR shots → HD 4K studio renders, bulk-parallel. For catalogues and Instagram at scale." },
  { icon: LibraryBig, title: "Catalog Suite", desc: "Tag, filter and lookbook every render your atelier ever made — searchable by style, stone and karat." },
  { icon: Shuffle, title: "Lasso Refinement", desc: "Mark a region, describe a change — Facetra re-renders only that region, pixel-locking the rest." },
  { icon: Scale, title: "Live-Gold-Rate BOM", desc: "Auto Bill-of-Materials at the current 22kt rate — client quotes never stale, karigar hours included." },
  { icon: Gem, title: "Gemstone & Metal Controls", desc: "Swap stones, karats and finishes without losing structure or silhouette." },
  { icon: Hammer, title: "Manufacturing-Friendly", desc: "Outputs respect setting depth, prong logic and casting feasibility." },
  { icon: Sparkles, title: "Jadau · Polki · Meenakari fluency", desc: "Native vocabulary — chaton, jaali, nakashi, meena reverse — rendered with intent." },
];

export default function Features() {
  return (
    <section id="features" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">Platform</p>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-white">
              Every step of the design floor,{" "}
              <span className="italic gold-gradient-text">re-imagined</span>.
            </h2>
          </div>
          <p className="text-white/55 text-sm md:text-base max-w-md">
            From the first scribble to the final CAD reference — Facetra fits where your atelier already
            works, only faster.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.04 }}
                data-testid={`feature-card-${i}`}
                className="glass-card rounded-2xl p-6 md:p-7 group"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D4AF37]/15 to-transparent border border-[#D4AF37]/25 flex items-center justify-center mb-5 group-hover:border-[#D4AF37]/60 transition-colors">
                  <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg md:text-xl text-white tracking-tight">{f.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
